import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { z } from 'zod';

import { KeyValueStorage } from './KeyValueStorage';

const subscribeToLocalStorage = (
  callback: (this: Window, ev: StorageEvent) => unknown,
): (() => void) => {
  window.addEventListener('storage', callback);
  return (): void => window.removeEventListener('storage', callback);
};

const getLocalStorageServerSnapshot = (): string | undefined => {
  throw Error('useLocalStorage is a client-only hook');
};

const baseSchema = z.union([
  z.boolean(),
  z.number(),
  z.string(),
  z.undefined(),
  z.record(z.unknown(), z.unknown()),
  z.array(z.unknown()),
]);

const supportedTypesSchema = z.union([
  baseSchema,
  z.record(baseSchema, baseSchema),
  z.array(baseSchema),
]);

export function useKeyValueStorage<
  T extends boolean | number | string | undefined | object,
>(
  keyValueStorage: KeyValueStorage,
  key: string,
  initialValue?: T,
): [T | undefined, (setterOrValue: T | ((value: T) => T)) => void] {
  const isT = useCallback(
    (value: unknown): value is T =>
      !(value === null || typeof value === 'function'),
    [],
  );

  const dispatchStorageEvent = useCallback(
    (_key: string, newValue: string | null) => {
      window.dispatchEvent(
        new StorageEvent('storage', { key: _key, newValue }),
      );
    },
    [],
  );

  const getKeyValueStorageItem = useCallback(
    (_key: string): string | undefined => keyValueStorage.getItem(_key),
    [keyValueStorage],
  );

  const setKeyValueStorageItem = useCallback(
    (_key: string, value: T): void => {
      const stringifiedValue = JSON.stringify(value);
      keyValueStorage.setItem(_key, stringifiedValue);
      dispatchStorageEvent(_key, stringifiedValue);
    },
    [dispatchStorageEvent, keyValueStorage],
  );

  const removeKeyValueStorageItem = useCallback(
    (_key: string): void => {
      keyValueStorage.removeItem(_key);
      dispatchStorageEvent(_key, null);
    },
    [dispatchStorageEvent, keyValueStorage],
  );

  const getSnapshot = useCallback(
    (): string | undefined => getKeyValueStorageItem(key),
    [getKeyValueStorageItem, key],
  );

  const store = useSyncExternalStore(
    subscribeToLocalStorage,
    getSnapshot,
    getLocalStorageServerSnapshot,
  );

  const setState = useCallback(
    (setterOrValue: T | ((state: T) => T)) => {
      const isFunction = (
        functionToCheck: T | ((state: T) => T),
      ): functionToCheck is (state: T) => T =>
        typeof functionToCheck === 'function';

      try {
        let nextState: T | undefined;

        if (isFunction(setterOrValue)) {
          const parsedValue: ReturnType<typeof supportedTypesSchema.safeParse> =
            store !== undefined
              ? supportedTypesSchema.safeParse(JSON.parse(store))
              : { success: true, data: undefined };

          if (parsedValue.success && isT(parsedValue.data)) {
            nextState = setterOrValue(parsedValue.data);
          }
        } else {
          nextState = setterOrValue;
        }

        if (nextState === undefined || nextState === null) {
          removeKeyValueStorageItem(key);
        } else {
          setKeyValueStorageItem(key, nextState);
        }
      } catch (e) {
        console.warn(e);
      }
    },
    [isT, key, removeKeyValueStorageItem, setKeyValueStorageItem, store],
  );

  useEffect(() => {
    if (
      getKeyValueStorageItem(key) === undefined &&
      typeof initialValue !== 'undefined'
    ) {
      setKeyValueStorageItem(key, initialValue);
    }
  }, [key, initialValue, getKeyValueStorageItem, setKeyValueStorageItem]);

  let finalValue = initialValue;

  if (store !== undefined) {
    const parsedValue = supportedTypesSchema.safeParse(JSON.parse(store));

    if (parsedValue.success && isT(parsedValue.data)) {
      finalValue = parsedValue.data;
    }
  }

  return [finalValue, setState];
}
